// Libraries
import { AppThunk } from '@redux/configureStore';
import { createSlice } from '@reduxjs/toolkit';
// Redux
import { setInformation, setOpenModalError } from '@redux/company/company.slice';
import { setPeriods as setPeriodsReport } from '@redux/reports/reports.slice';
import { showLoader } from '@redux/utils/utils.slice';
// Types
import { IAppThunkTaxPayerInformation, IPilaState } from './types';
// Api
import {
    apiGetTaxPayer,
    apiPostCreatePilaUser,
    apiPostPaymentSocialSecurityPila,
    apiPostSummaryTemplatePila,
    apiPostGetStateTemplate,
    apiPostTaxPayerByDocument,
    apiPostTaxpayerPrechargedInformation,
} from '@api/pila';
import { apiGetPeriodsByYear } from '@api/utils';
import { apiGetAllPeriodicity } from '@api/payrollAdjuster';
// Constant
import { INITIAL_DATE } from '@pages/payroll-adjuster/constants/InitialDate';
import { PeriodicityTypes } from '@constants/Periodicity';
import { ERROR_KEY, ERROR_SPREEDSHEET_PAID } from '@pages/pila-detail/constants/PilaDetail';
// Models
import { IPeriods } from '@models/Periods';
import { IErrorPilaUser, IResponsePaymentSocialSecurity } from '@models/Pila';
import { IFormikContributorValues } from '@pages/company/components/models/FormikConfig';
import { IHandleSearchTaxpayerParams } from '@models/TaxPayer';
import { PilaResponseErrorCodes } from '@constants/responseCodes/pila';
import { GENERIC_ERROR_MESSAGE } from '@constants/error';

const initialState: IPilaState = {
    pila: [],
    periods: [],
    showCustomLoader: false,
    currentPeriod: {
        id: '',
        indexMonth: INITIAL_DATE,
        code: '',
        name: '',
        start_date: '',
        end_date: '',
        year_month_id: '',
        periodicity_id: '',
        yearPeriods: [],
        period: {
            id: '',
            code: '',
            name: '',
            start_date: '',
            end_date: '',
            year_month_id: '',
            periodicity_id: '',
        },
        created_at: '',
        updated_at: '',
    },
    modalPaymentSocialSecurity: {
        visible: false,
        response: '',
    },
    stateTemplate: '',
    templateSelected: null,
    taxPayer: null,
    taxpayerData: null,
    error: '',
    reliquidate: false,
    completedLiquidation: false,
};

const pilaSlice = createSlice({
    name: 'pila',
    initialState,
    reducers: {
        setPeriods: (state, action) => {
            state.periods = action.payload;
        },
        setPeriod: (state, action) => {
            state.currentPeriod = { ...state.currentPeriod, period: action.payload };
        },
        setCurrentPeriod: (state, action) => {
            state.currentPeriod = action.payload;
        },
        setPila: (state, action) => {
            state.pila = action.payload;
        },
        setModalPaymentSocialSecurity: (state, action) => {
            state.modalPaymentSocialSecurity = action.payload;
        },
        setTemplateSelected: (state, action) => {
            state.templateSelected = action.payload;
        },
        setTaxPayer: (state, action) => {
            state.taxPayer = action.payload;
        },
        setTaxPayerData: (state, action) => {
            state.taxpayerData = action.payload;
        },
        setStateTemplate: (state, action) => {
            state.stateTemplate = action.payload;
        },
        setCustomLoader: (state, action) => {
            state.showCustomLoader = action.payload;
        },
        setReliquidate: (state, action) => {
            state.reliquidate = action.payload;
        },
        setCompletedLiquidation: (state, action) => {
            state.completedLiquidation = action.payload;
        },
        failedRequest: (state, action) => {
            state.error = action.payload;
        },
    },
});

/**
 * Action to get periods by periodicity.
 */
export const getAllPeriodsInPila = (): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            company: { information },
            payrollAdjuster: { periods },
        } = getState();

        if (!periods.length) {
            try {
                const response = await apiGetAllPeriodicity<IPeriods[]>(information?.periodicity_id ?? PeriodicityTypes.MONTHLY);
                const periods = response.map(value => ({
                    ...value,
                    period: (value.yearPeriods && value.yearPeriods[INITIAL_DATE]) ?? {},
                    indexMonth: INITIAL_DATE,
                }));
                dispatch(setPeriods(periods));
                dispatch(setCurrentPeriod(periods[INITIAL_DATE]));
            } catch (error) {
                dispatch(failedRequest('Error request data'));
            }
        }
    };
};

/**
 * Action to get state summary templates pila
 */
export const postGetState = (id: string): AppThunk => {
    return async (dispatch): Promise<void | unknown> => {
        try {
            const data = await apiPostGetStateTemplate({
                year_period_id: id,
            });
            dispatch(setStateTemplate(data));
            return data;
        } catch (error) {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Action to get summary templates pila
 */
export const postDataPila = (id: string): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            if (id) {
                const data = await apiPostSummaryTemplatePila({
                    year_period_id: id,
                });
                dispatch(setPila(Array.isArray(data) ? data : []));
                dispatch(setCustomLoader(false));
            }
        } catch (error) {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Action to get taxpayer
 */
export const getTaxPayer = (): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            company: {
                information: { pila_user },
            },
        } = getState();
        try {
            if (pila_user) {
                const data = await apiGetTaxPayer();
                dispatch(setTaxPayer(data));
            }
        } catch (error) {
            dispatch(failedRequest('Error request data'));
        }
    };
};

export const getTaxpayerPrechargedInformation = (
    pilaUser: string,
    pilaPassword: string
): AppThunk<Promise<IAppThunkTaxPayerInformation>> => {
    return async (dispatch): Promise<IAppThunkTaxPayerInformation> => {
        try {
            const response = await apiPostTaxpayerPrechargedInformation({ pilaUser, pilaPassword });
            if (response.code === PilaResponseErrorCodes.PILA_SUCCESS_OPERATION && response.data) {
                const { economicActivity, arl, exemptParafiscalContributions } = response.data;
                return {
                    arl,
                    economicActivity,
                    exemptParafiscals: exemptParafiscalContributions,
                };
            }

            if (response.code === PilaResponseErrorCodes.PILA_AUTH_ERROR) {
                dispatch(
                    setOpenModalError({
                        message: 'El usuario o la contraseña ingresados son incorrectos.',
                        open_modal: true,
                    })
                );
                return {
                    arl: '',
                    economicActivity: '',
                    exemptParafiscals: false,
                };
            }

            dispatch(
                setOpenModalError({
                    message: 'En este momento no se pudo procesar la información.',
                    open_modal: true,
                })
            );

            return {
                arl: '',
                economicActivity: '',
                exemptParafiscals: false,
            };
        } catch (error) {
            dispatch(failedRequest('Error request data'));
            dispatch(
                setOpenModalError({
                    message: 'En este momento no se pudo procesar la información.',
                    open_modal: true,
                })
            );
            return {
                arl: '',
                economicActivity: '',
                exemptParafiscals: false,
            };
        }
    };
};

/**
 * Action to get taxpayer by document
 */
export const getTaxPayerByDocument = (data: IHandleSearchTaxpayerParams): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            company: {
                information: { pila_user },
            },
        } = getState();
        try {
            if (pila_user) {
                const { data: responseTaxpayer } = await apiPostTaxPayerByDocument(data);
                dispatch(setTaxPayerData(responseTaxpayer));
            }
        } catch (error) {
            dispatch(failedRequest('Error request data'));
        }
    };
};

export const createPilaUser = (
    data: IFormikContributorValues,
    handleOpenErrorModal: (errors: IErrorPilaUser[]) => void,
    handleCloseRegister: () => void,
    handleConfirmModal: () => void,
    resetForm: () => void
): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const { company } = getState();
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const response = await apiPostCreatePilaUser(data);
            const code = response.code;
            const errorParam: IErrorPilaUser[] = [
                {
                    Codigo: code,
                    ListaMensaje: null,
                    Texto: response.data?.includes('Ya existe')
                        ? 'Ya existe un código y/o nombre de sucursal asociado a ese número y Tipo de identificación.'
                        : response.data || GENERIC_ERROR_MESSAGE,
                    Tipo: 'Error',
                },
            ];

            if (
                code == PilaResponseErrorCodes.PILA_TAXPAYER_CREATION_FAILED ||
                code === PilaResponseErrorCodes.PILA_INTERNAL_SERVER_ERROR ||
                response.data?.includes('Ya existe')
            ) {
                handleOpenErrorModal(errorParam);
                return;
            }

            if (code === PilaResponseErrorCodes.PILA_SUCCESS_OPERATION) {
                handleConfirmModal();
            }

            handleCloseRegister();
            resetForm();
            dispatch(setInformation({ ...company.information, pila_user: data.user.user_name }));
        } catch (error) {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Action to payment social security pila
 */
export const postPaymentSocialSecurityPila = (): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            pila: {
                currentPeriod: { id },
            },
        } = getState();
        try {
            const response = await apiPostPaymentSocialSecurityPila({
                year_period_id: id,
            });

            if (response.code === PilaResponseErrorCodes.PILA_SUCCESS_OPERATION && response.data) {
                const spreedsheetInformation = response.data;
                const mappedInformation: IResponsePaymentSocialSecurity = {
                    EstadoLiquidacion: spreedsheetInformation.liquidationStatus,
                    FechaLimitePago: spreedsheetInformation.paymentDueDate,
                    FechaRealPago: spreedsheetInformation.actualPaymentDate,
                    PeriodoPension: spreedsheetInformation.pensionPeriod,
                    Planilla: spreedsheetInformation.payrollNumber,
                    TotalPagar: spreedsheetInformation.totalAmount,
                    UrlPago: spreedsheetInformation.paymentUrl,
                };
                dispatch(setModalPaymentSocialSecurity({ visible: true, response: mappedInformation }));
            } else {
                if (response.code === PilaResponseErrorCodes.PILA_SPREEDSHEET_NOT_FOUND) {
                    dispatch(setModalPaymentSocialSecurity({ visible: true, response: ERROR_KEY }));
                    return;
                }
                dispatch(setModalPaymentSocialSecurity({ visible: true, response: ERROR_SPREEDSHEET_PAID }));
            }
        } catch (error) {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Action to get all periods for pila
 */
export const getPeriodsPilaByYear = (
    handleStateTemplate: (id: string, intervalId?: NodeJS.Timer) => void,
    isOpenPagePila = false
): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            periods: { currentPeriod, periods },
        } = getState();
        try {
            showLoader(true);
            const actualYear = periods[currentPeriod.indexMonth ?? new Date().getMonth()];
            const actualYearFormat = Number(actualYear.code.substring(0, 4));
            const response = await apiGetPeriodsByYear<IPeriods[]>(String(actualYearFormat));
            if (!Array.isArray(response)) {
                dispatch(setPeriodsReport([]));
                return;
            }
            const monthSelected = Number(actualYear.code.substring(5, 7)) - 1;
            const currentMonth = response[monthSelected ?? new Date().getMonth()];
            dispatch(setPeriodsReport(response));
            const intervalId = setInterval(async () => {
                await handleStateTemplate(currentMonth.id, intervalId);
            }, 2000);
            showLoader(false);
        } catch (error) {
            showLoader(false);
            dispatch(failedRequest('Error request'));
        }
    };
};

export const {
    setPeriods,
    setPeriod,
    setCurrentPeriod,
    setPila,
    setTemplateSelected,
    setTaxPayer,
    setTaxPayerData,
    setModalPaymentSocialSecurity,
    setStateTemplate,
    setCustomLoader,
    setReliquidate,
    setCompletedLiquidation,
    failedRequest,
} = pilaSlice.actions;

export default pilaSlice.reducer;
